const GOOGLE_SIGNIN_PENDING_KEY = 'amplify_google_signin_pending';
const AUTH_ERROR_STORAGE_KEY = 'amplify_auth_error';

export function loadStoredAuthError() {
  return sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY) || '';
}

export function storeAuthError(message) {
  if (message) {
    sessionStorage.setItem(AUTH_ERROR_STORAGE_KEY, message);
  } else {
    sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);
  }
}

/** Set when user starts Google OAuth (cleared on success or handled failure). */
export function markGoogleSignInPending() {
  sessionStorage.setItem(GOOGLE_SIGNIN_PENDING_KEY, String(Date.now()));
}

export function clearGoogleSignInPending() {
  sessionStorage.removeItem(GOOGLE_SIGNIN_PENDING_KEY);
}

export function wasGoogleSignInPending(maxAgeMs = 15 * 60 * 1000) {
  const raw = sessionStorage.getItem(GOOGLE_SIGNIN_PENDING_KEY);
  if (!raw) return false;
  const started = Number(raw);
  if (!Number.isFinite(started)) return true;
  return Date.now() - started < maxAgeMs;
}

function decodeOAuthDescription(value) {
  if (!value) return '';
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

function isDuplicateAccountMessage(text) {
  const lower = text.toLowerCase();
  return (
    lower.includes('already exists') ||
    lower.includes('already registered') ||
    lower.includes('aliasexists') ||
    lower.includes('usernameexistsexception') ||
    lower.includes('user already exists') ||
    lower.includes('account exists') ||
    lower.includes('external provider') ||
    lower.includes('federated') ||
    lower.includes('signinmethods') ||
    lower.includes('userexists') ||
    lower.includes('cannot be registered')
  );
}

function messageFromUrlParams(searchParams) {
  const error = searchParams.get('error');
  const description = decodeOAuthDescription(searchParams.get('error_description'));

  if (!error && !description) return null;

  const combined = `${error || ''} ${description}`.trim();

  if (error === 'access_denied' || combined.toLowerCase().includes('cancel')) {
    return 'Google sign-in was cancelled. You can try again or sign in with email and password.';
  }

  if (isDuplicateAccountMessage(combined)) {
    return (
      'This email already has an Amplify Loyalty account created with email and password. ' +
      'Sign in with your password below instead of Google.'
    );
  }

  if (description) {
    return description;
  }

  return 'Google sign-in could not be completed. Try again or use email and password.';
}

function isAxiosLikeError(error) {
  return Boolean(error?.response || error?.isAxiosError);
}

/** Map /app/me and other API failures to clear login-page copy (never raw axios text). */
function messageFromApiError(error) {
  if (!isAxiosLikeError(error)) return null;

  const status = error.response?.status;
  const data = error.response?.data;
  const apiMessage =
    (typeof data === 'string' ? data : '') ||
    data?.message ||
    data?.error ||
    '';

  const combined = `${apiMessage} ${error.message || ''}`.trim();

  if (
    status === 409 ||
    isDuplicateAccountMessage(combined) ||
    (error.code === 'ERR_BAD_REQUEST' && isDuplicateAccountMessage(combined))
  ) {
    return (
      'An account with this email already exists. Sign in with email and password instead of Google.'
    );
  }

  if (status === 401) {
    return 'Your session expired. Please sign in again.';
  }

  if (status === 404) {
    return (
      'No Amplify Loyalty account was found for this sign-in. Create an account below or sign in with email and password.'
    );
  }

  if (status === 400 && apiMessage && !apiMessage.includes('status code')) {
    return apiMessage;
  }

  if (status >= 500) {
    if (isDuplicateAccountMessage(combined)) {
      return (
        'An account with this email already exists. Sign in with email and password instead of Google.'
      );
    }
    return 'Something went wrong on our side. Please try again or sign in with email and password.';
  }

  if (apiMessage && !apiMessage.toLowerCase().includes('status code')) {
    return apiMessage;
  }

  return null;
}

function messageFromError(error) {
  if (!error) return null;

  const fromApi = messageFromApiError(error);
  if (fromApi) return fromApi;

  const message = error.message || String(error);
  if (message.toLowerCase().includes('status code')) {
    return 'Sign-in could not be completed. Try email and password, or contact support if this continues.';
  }
  const recovery = error.recoverySuggestion || '';
  const combined = `${message} ${recovery}`;

  if (message.toLowerCase().includes('user cancelled oauth')) {
    return 'Google sign-in was cancelled. You can try again or sign in with email and password.';
  }

  if (isDuplicateAccountMessage(combined)) {
    return (
      'This email already has an Amplify Loyalty account created with email and password. ' +
      'Sign in with your password below instead of Google.'
    );
  }

  if (error.name === 'OAuthSignInException' || error.name === 'OAuthSignInError') {
    return message || 'Google sign-in could not be completed. Try again or use email and password.';
  }

  return message || 'Sign-in failed. Please try again.';
}

/** Message when user returns from Cognito without completing OAuth. */
export function googleSignInAbandonedMessage() {
  return (
    'Google sign-in did not finish. If this email already has a password account, ' +
    'sign in with email and password below.'
  );
}

/**
 * Resolve a user-facing auth error from OAuth redirect URL and/or Amplify error.
 */
export function resolveAuthErrorMessage({ error = null, searchParams = null } = {}) {
  if (searchParams) {
    const fromUrl = messageFromUrlParams(searchParams);
    if (fromUrl) return fromUrl;
  }
  const fromApi = messageFromApiError(error);
  if (fromApi) return fromApi;
  return messageFromError(error);
}

/** Shown when /app/me succeeds but the payload is not usable for app access. */
export function invalidProfileAfterSignInMessage() {
  return (
    'We could not load your Amplify Loyalty account. If you are new, create an account. ' +
    'Otherwise sign in with the email and password you used when you signed up.'
  );
}

export function hasOAuthCallbackParams(search = '') {
  const params = new URLSearchParams(search || window.location.search);
  return Boolean(params.get('code') && params.get('state'));
}

export function stripOAuthQueryFromPath(search, { keepErrors = false } = {}) {
  const params = new URLSearchParams(search);
  const oauthKeys = keepErrors
    ? ['code', 'state']
    : ['code', 'state', 'error', 'error_description'];
  let changed = false;
  oauthKeys.forEach((key) => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });
  if (!changed) return null;
  const query = params.toString();
  return query ? `?${query}` : '';
}
